import React from 'react';

import { ThemeProvider, createTheme } from '@mui/material/styles';

const defaultTheme = createTheme({
  typography: {
    fontFamily: [
      'Londrina Solid',
      'sans-serif',
    ].join(','),
    fontSize: 16,
    h1: {
      fontFamily: '"Genty Demo Regular", "Genty Demo Regular Placeholder", sans-serif',
      fontWeight: 400,
      lineHeight: 1,
    },
    h1Secondary: {
      fontFamily: '"Genty Demo Regular", "Genty Demo Regular Placeholder", sans-serif',
      fontWeight: 400,
      lineHeight: 1,
    },
    h2: {
      fontFamily: '"Londrina Solid", sans-serif',
      fontWeight: 400,
      lineHeight: 1.2,
    },
    body1: {
      color: '#ffffff',
      lineHeight: 1.4,
    },
    body2: {
      color: '#ffffff',
      fontSize: '1.25rem',
    },
  },
  palette: {
    background: {
      default: "#005fff",
    },
    primary: {
      main: '#005fff',
    },
    secondary: {
      main: '#121212',
    },
    tertiary: {
      main: '#fec901',
    },
    quaternary: {
      main: '#ffffff',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 500,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

defaultTheme.shadows.push('0 0px 8px 0px rgba(65, 65, 65, 0.4)');

const finalTheme = createTheme(defaultTheme, {
  typography: {
    h1: {
      [defaultTheme.breakpoints.down('sm')]: {
        fontSize: '3.25rem',
      },
      [defaultTheme.breakpoints.between('sm', 'lg')]: {
        fontSize: '5rem',
      },
      [defaultTheme.breakpoints.up('lg')]: {
        fontSize: '8rem',
      },
    },
    h1Secondary: {
      [defaultTheme.breakpoints.down('sm')]: {
        fontSize: '3.25rem',
      },
      [defaultTheme.breakpoints.between('sm', 'lg')]: {
        fontSize: '4rem',
      },
      [defaultTheme.breakpoints.up('lg')]: {
        fontSize: '5rem',
      },
    },
    h2: {
      [defaultTheme.breakpoints.down('sm')]: {
        fontSize: '2.25rem',
      },
      [defaultTheme.breakpoints.between('sm', 'lg')]: {
        fontSize: '3rem',
      },
      [defaultTheme.breakpoints.up('lg')]: {
        fontSize: '4rem',
      },
    },
    body1: {
      [defaultTheme.breakpoints.down('sm')]: {
        fontSize: '1.5rem',
      },
      [defaultTheme.breakpoints.between('sm', 'lg')]: {
        fontSize: '1.75rem',
      },
      [defaultTheme.breakpoints.up('lg')]: {
        fontSize: '2rem',
      },
    },
  },
  components: {
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1Secondary: 'h1',
        }
      },
    },
    MuiLink: {
      variants: [
        {
          props: { 
            variant: 'link1',
          },
          style: {
            color: '#ffffff',
            textDecoration: 'underline',
            transition: 'color .2s ease-out',
            '&:hover': {
              color: '#fec901',
            },
          },
        },
      ],
    },
    MuiIconButton: {
      variants: [
        {
          props: { 
            variant: 'pageIcon1',
          },
          style: {
            color: 'rgba(18, 18, 18, 1)',
            backgroundColor: 'rgba(255, 255, 255, 1)',
            boxShadow: '0 0px 10px 0px rgba(254, 201, 1, 0.6)',
            '&:hover': {
              backgroundColor: 'rgba(254, 201, 1, 0.85)',
              boxShadow: 25,
            },
            [defaultTheme.breakpoints.down('sm')]: {
              fontSize: '1.75rem',
              padding: '0.375rem',
            },
            [defaultTheme.breakpoints.up('sm')]: {
              fontSize: '2.25rem',
              padding: '0.5rem',
            },
          },
        },
        {
          props: { 
            variant: 'ctrlIcon1',
          },
          style: {
            color: 'rgba(255,255,255, 1)',
            backgroundColor: 'rgba(18, 18, 18, 1)',
            transition: 'all 0.25s ease',
            padding: '0.25rem',
            '&:hover': {
              backgroundColor: 'rgba(254, 201, 1, 1)',
              boxShadow: 25,
            },
            [defaultTheme.breakpoints.down('sm')]: {
              fontSize: '2rem',
            },
            [defaultTheme.breakpoints.up('sm')]: {
              fontSize: '2.75rem',
            },
          },
        },
        {
          props: { 
            variant: 'linkIcon1',
          },
          style: {
            color: 'rgba(18, 18, 18, 1)',
            display: 'block',
            backgroundColor: 'rgba(255, 255, 255, 1)',
            boxShadow: '0 0px 10px 0px rgba(255, 255, 255, 0.6)',
            transition: 'all 0.25s ease',
            // '& .MuiTouchRipple-root .MuiTouchRipple-child': { // Only uncomment if changing the borderRadius to 0
            //   borderRadius: 0
            // },
            '&:hover': {
              // color: 'rgba(255, 255, 255, 1)',
              boxShadow: '0 0px 10px 0px rgba(160, 196, 255, 0.6)',
              backgroundColor: 'rgba(254, 201, 1, 1)',
            },
            '& svg': {
              display: 'block',
              height: 'auto',
            },
            [defaultTheme.breakpoints.down('sm')]: {
              padding: '0.4375rem',
              '& svg': {
                width: '1.625rem',
              },
            },
            [defaultTheme.breakpoints.up('sm')]: {
              padding: '0.5rem',
              '& svg': {
                width: '2.25rem',
              },
            },
          },
        },
      ],
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '& .MuiTypography-root': {
            [defaultTheme.breakpoints.down('md')]: {
              fontSize: '2rem',
            },
            [defaultTheme.breakpoints.up('md')]: {
              fontSize: '2.5rem',
            },
          },
        },
      },
    },
  },
});

// console.log(finalTheme);

const Theme = ({ children }) => {
  return (
    <ThemeProvider theme={finalTheme}>
      {children}
    </ThemeProvider>
  );
};

export default Theme;
